/**
 * Shared description of the background-field layers.
 *
 * The layer picker, the sidebar legend and the on-map legend all read from
 * here. They were drifting apart otherwise — a rename in one place left the
 * others describing the same colours under a different name, which on a
 * safety-adjacent tool is worse than merely untidy.
 */

import {
  GLIDE_WORST_RATIO,
  GUST_DISPLAY_MAX,
  HAZARD_COLOURS,
  HAZARD_LABELS,
  TI_DISPLAY_MAX,
  glideAtRampPosition,
  gustAtRampPosition,
  hikeFlyRamp,
  rgbToCss,
  speedUpAtRampPosition,
  speedUpRamp,
  turbulenceRamp,
  watermarkRamp,
} from '../render/colormaps';
import type { RasterMode } from '../render/flowRenderer';
import type { ProviderId } from '../dem/types';
import { FALLBACK_PROVIDER } from '../dem/resolveDEM';

/** Names as the pilot sees them. */
export const RASTER_LABELS: Record<RasterMode, string> = {
  hazard: 'Air character',
  gust: 'Gust m/s',
  speedup: 'Wind speed variation',
  turbulence: 'Terrain turbulence',
  watermark: 'Below altitude',
  hikefly: 'Hike & fly',
  none: 'None',
};

/**
 * Order in the picker: most decision-shaped first, most diagnostic last, then
 * the two that describe ground rather than air.
 *
 * Air character triages a day, gust grades it, the next two explain why. The
 * last pair answer a different question altogether — where to be, rather than
 * what the air is doing — so they sit apart from the sequence above them.
 */
export const RASTER_ORDER: RasterMode[] = [
  'hazard',
  'gust',
  'speedup',
  'turbulence',
  'watermark',
  'hikefly',
  'none',
];

/** The layers describing terrain rather than air; the picker groups these. */
export const TERRAIN_RASTERS: RasterMode[] = ['watermark', 'hikefly'];

/** One-line explanation of what the layer measures. */
export const RASTER_SUBTITLES: Partial<Record<RasterMode, string>> = {
  hazard: 'gust and separation combined',
  gust: 'absolute fluctuation σᵤ',
  speedup: 'local wind ÷ free stream',
  turbulence: 'σᵤ/U — independent of wind strength',
  watermark: 'flow below the chosen altitude',
  hikefly: 'glide needed to reach open ground',
};

/** CSS gradient matching the shader ramp for a raster mode. */
export function rasterRampCss(mode: RasterMode): string {
  const stops: string[] = [];
  for (let i = 0; i <= 12; i++) {
    const t = i / 12;
    const rgb =
      mode === 'speedup'
        ? speedUpRamp(t)
        : mode === 'watermark'
          ? watermarkRamp(t)
          : mode === 'hikefly'
            ? hikeFlyRamp(t)
            : turbulenceRamp(t);
    stops.push(`${rgbToCss(rgb)} ${((t * 100) | 0)}%`);
  }
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

/** Left / centre / right tick labels for the continuous rasters. */
export function rasterScaleLabels(mode: RasterMode): [string, string, string] | null {
  switch (mode) {
    case 'speedup':
      return [
        `${speedUpAtRampPosition(0).toFixed(1)}× sheltered`,
        '1.0×',
        `${speedUpAtRampPosition(1).toFixed(1)}× compressed`,
      ];
    case 'gust':
      // Logarithmic, so the midpoint is not half the maximum.
      return ['0 smooth', `${gustAtRampPosition(0.5).toFixed(1)} m/s`, `${GUST_DISPLAY_MAX} m/s`];
    case 'turbulence':
      return ['0 smooth', `${(TI_DISPLAY_MAX / 2).toFixed(2)}`, `${TI_DISPLAY_MAX} detached`];
    case 'watermark':
      return [
        `${speedUpAtRampPosition(0).toFixed(1)}× sheltered`,
        '1.0×',
        `${speedUpAtRampPosition(1).toFixed(1)}× channelled`,
      ];
    case 'hikefly':
      // Written as pilots quote glide, and low is good — so the scale runs
      // from marginal on the left to a clean drop on the right.
      return [
        `1:${GLIDE_WORST_RATIO} marginal`,
        `1:${glideAtRampPosition(0.5).toFixed(1)}`,
        `1:${glideAtRampPosition(1).toFixed(1)} clean drop`,
      ];
    default:
      return null;
  }
}

/**
 * Layers whose values come out of the turbulence model.
 *
 * That model runs on terrain roughness detrended from the DEM, which amplifies
 * DEM noise: the roughness feeds z0, z0 feeds the surface-layer turbulence
 * term, and that feeds both the turbulence intensity and every hazard class
 * above "rough". On a coarse, quantised global DEM the result is not
 * trustworthy, so these two are withheld rather than shown as though they
 * meant something.
 *
 * The wind-speed and gust layers are not gated. Speed-up comes from the
 * mass-conserving Layer 1 solve, which depends on the DEM only through terrain
 * *shape* and is far less sensitive to its noise.
 */
export const TURBULENCE_DERIVED_RASTERS: RasterMode[] = ['hazard', 'turbulence'];

/** Where a gated layer sends you instead: the one least sensitive to DEM noise. */
export const RASTER_FALLBACK: RasterMode = 'speedup';

/**
 * Whether a layer can be trusted on the elevation source that actually
 * answered — not the one that was requested, which may have been unavailable.
 */
export function rasterAvailableFor(
  mode: RasterMode,
  provider: ProviderId | null
): boolean {
  if (provider !== FALLBACK_PROVIDER) return true;
  return !TURBULENCE_DERIVED_RASTERS.includes(mode);
}

/** Discrete swatches for the hazard raster, which has classes rather than a ramp. */
export function hazardSwatches(): { label: string; colour: string }[] {
  return HAZARD_LABELS.map((label, i) => ({ label, colour: rgbToCss(HAZARD_COLOURS[i]) }));
}
